import type {
  RecurringDonationSeries,
  RecurringFrequency,
} from '@/types/recurringDonation'
import { parseUkIsoDate, UK_TIME_ZONE } from '@/helpers/recurring/dates'
import { RECURRING_BACKFILL_HARD_LIMIT } from '@/helpers/recurring/limits'

/**
 * Calendar recurrence from a fixed anchor date.
 *
 * Always add N periods to the original anchor rather than chaining from the
 * previous occurrence. That keeps a 31 January monthly gift on 31 Mar after
 * February, instead of drifting to the 28th.
 */
export function occurrenceAtIndex(
  anchorIso: string,
  frequency: RecurringFrequency,
  index: number
): string {
  const anchor = parseUkIsoDate(anchorIso)
  const next = addPeriods(anchor, frequency, index)
  return next.toISODate() ?? anchorIso
}

function addPeriods(
  anchor: ReturnType<typeof parseUkIsoDate>,
  frequency: RecurringFrequency,
  count: number
) {
  if (frequency === 'weekly') {
    return anchor.plus({ weeks: count })
  }
  if (frequency === 'monthly') {
    return addCalendarMonths(anchor, count)
  }
  if (frequency === 'quarterly') {
    return addCalendarMonths(anchor, count * 3)
  }
  return addCalendarMonths(anchor, count * 12)
}

/**
 * Luxon's plus({ months }) from a fixed start keeps the intended day-of-month
 * when that day exists, and clamps to the last day of shorter months.
 */
function addCalendarMonths(
  anchor: ReturnType<typeof parseUkIsoDate>,
  months: number
) {
  const shifted = anchor.plus({ months })
  const anchorDay = anchor.day
  const daysInMonth = shifted.daysInMonth ?? 28
  const day = Math.min(anchorDay, daysInMonth)
  return shifted.set({ day }).setZone(UK_TIME_ZONE).startOf('day')
}

/** First occurrence strictly after `afterIso`, computed from the anchor. */
export function nextOccurrenceAfter(
  anchorIso: string,
  frequency: RecurringFrequency,
  afterIso: string
): string | null {
  const maxIndex = RECURRING_BACKFILL_HARD_LIMIT + 8
  for (let index = 0; index <= maxIndex; index += 1) {
    const date = occurrenceAtIndex(anchorIso, frequency, index)
    if (date > afterIso) return date
  }
  return null
}

/** Inclusive list of due dates from `fromIso` through `throughIso`. */
export function enumerateOccurrenceDates(input: {
  anchorIso: string
  frequency: RecurringFrequency
  fromIso: string
  throughIso: string
  hardLimit?: number
}): string[] {
  const hardLimit = input.hardLimit ?? RECURRING_BACKFILL_HARD_LIMIT
  const dates: string[] = []
  const maxIndex = hardLimit + 8

  for (let index = 0; index <= maxIndex; index += 1) {
    const date = occurrenceAtIndex(input.anchorIso, input.frequency, index)
    if (date > input.throughIso) break
    if (date >= input.fromIso) {
      dates.push(date)
      if (dates.length > hardLimit) break
    }
  }

  return dates
}

/**
 * Last occurrence the current rules will produce before the series ends.
 *
 * End date is a cut-off, not a donation date. A monthly gift that started on
 * 17 July 2024 and ends on 12 September 2026 has a final donation of
 * 17 August 2026, not 12 September.
 */
export function finalScheduledOccurrenceDate(
  series: Pick<
    RecurringDonationSeries,
    'frequency' | 'scheduleAnchorDate' | 'endCondition'
  >
): string | null {
  if (series.endCondition.type === 'until_stopped') return null

  if (series.endCondition.type === 'on_date') {
    const dates = enumerateOccurrenceDates({
      anchorIso: series.scheduleAnchorDate,
      frequency: series.frequency,
      fromIso: series.scheduleAnchorDate,
      throughIso: series.endCondition.endDate,
    })
    return dates.at(-1) ?? null
  }

  const lastIndex = series.endCondition.occurrenceCount - 1
  if (lastIndex < 0) return null
  return occurrenceAtIndex(
    series.scheduleAnchorDate,
    series.frequency,
    lastIndex
  )
}
